/**
 * useMigrationStream — owns the SSE migration channel for a list.
 *
 * The migration channel backfills intent metadata (parsed_intent /
 * intent_embeddings) on legacy free-text items. It is deliberately SEPARATE
 * from useProgressStream: different event schema (MigrationEvent), different
 * overlay UI, different terminal behavior. The execution contract shared with
 * list-execute-sdd is never touched here.
 *
 * Lifecycle:
 *  - connects on mount only when `enabled` is true (the page computes it from
 *    `migration_required` on the loaded detail)
 *  - terminal `migration_completed` → status "completed" + `onCompleted()`
 *  - terminal `migration_partial` → status "partial" + `onPartial(failedCount)`
 *    (no auto retry — the page must NOT re-open the stream after a partial)
 *  - stream error → status "error" + `onError()`; `retry()` restarts manually
 *  - cleanup on unmount / enabled flip closes the stream via the service cleanup
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { fetchMigrationStream } from "modules/lists/services/listService";
import {
    MigrationState,
    MigrationStreamStatus,
} from "modules/lists/types/lists";

export interface UseMigrationStreamOptions {
    /** UUID of the list being migrated. */
    listId: string;
    /**
     * When false the hook never connects. The page passes
     * `selectedList.migrationRequired === true`.
     */
    enabled?: boolean;
    /** Invoked once when the terminal migration_completed event arrives. */
    onCompleted?: () => void;
    /** Invoked once when the terminal migration_partial event arrives. */
    onPartial?: (failedCount: number) => void;
    /** Invoked once when the stream fails before a terminal event. */
    onError?: () => void;
}

export interface MigrationStreamController {
    /** Lifecycle status of the migration stream. */
    status: MigrationStreamStatus;
    /** Latest parsed migration event (null until the first event arrives). */
    state: MigrationState | null;
    /** Restarts the stream (manual retry after an error). */
    retry: () => void;
}

/**
 * Opens and manages the migration SSE stream for a list.
 * @param options Hook options (listId, enabled, terminal callbacks).
 * @returns MigrationStreamController with status, latest event state and retry.
 */
export const useMigrationStream = ({
    listId,
    enabled = true,
    onCompleted,
    onPartial,
    onError,
}: UseMigrationStreamOptions): MigrationStreamController => {
    const [status, setStatus] = useState<MigrationStreamStatus>("idle");
    const [state, setState] = useState<MigrationState | null>(null);
    const [retryNonce, setRetryNonce] = useState(0);
    // Callbacks in a ref so the effect never re-runs when the caller passes
    // inline handlers (e.g. the page's useCallback-free arrow functions).
    const callbacksRef = useRef({ onCompleted, onPartial, onError });
    callbacksRef.current = { onCompleted, onPartial, onError };

    useEffect(() => {
        if (!enabled || !listId) return;
        setStatus("streaming");
        setState(null);
        const cleanup = fetchMigrationStream(
            listId,
            (next) => {
                setState(next);
                if (next.step === "migration_item") {
                    setStatus("streaming");
                    return;
                }
                if (next.step === "migration_completed") {
                    setStatus("completed");
                    callbacksRef.current.onCompleted?.();
                    return;
                }
                // migration_partial — terminal, never auto-retried.
                setStatus("partial");
                callbacksRef.current.onPartial?.(next.failedCount ?? 0);
            },
            () => {
                setStatus("error");
                callbacksRef.current.onError?.();
            },
        );
        return cleanup;
    }, [listId, enabled, retryNonce]);

    const retry = useCallback(() => {
        setRetryNonce((nonce) => nonce + 1);
    }, []);

    return { status, state, retry };
};
